import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, Building2, Globe, Users, Briefcase, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import JobCard from "@/components/jobs/JobCard";

export default function CompanyProfilePage() {
  const { recruiterId } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/recruiter/company/${recruiterId}`)
      .then((r) => setProfile(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [recruiterId]);

  const handleToggleSave = (jobId, saved) => {
    setProfile((p) => ({ ...p, jobs: p.jobs.map((j) => (j.id === jobId ? { ...j, is_saved: saved } : j)) }));
  };

  if (loading) {
    return <div className="flex items-center justify-center py-24" data-testid="company-profile-loading"><Loader2 className="w-6 h-6 text-indigo-400 animate-spin" /></div>;
  }

  if (error || !profile) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="company-profile-not-found">
        <p className="text-slate-400 text-sm">Company profile not found.</p>
        <Link to="/jobs" className="text-indigo-400 text-sm mt-3 inline-block">Back to Job Board</Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8" data-testid="company-profile-page">
      <Link to="/jobs" data-testid="company-profile-back-link" className="flex items-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-white mb-6">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Job Board
      </Link>

      <div className="glass-panel rounded-2xl p-6 mb-6">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-xl bg-indigo-500/10 flex items-center justify-center shrink-0">
            <Building2 className="w-7 h-7 text-indigo-400" />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-semibold text-white" data-testid="company-profile-name">{profile.company_name}</h1>
            {profile.tagline && <p className="text-sm text-indigo-400 mt-0.5" data-testid="company-profile-tagline">{profile.tagline}</p>}
            <div className="flex items-center gap-4 mt-2 text-xs text-slate-400 flex-wrap">
              {profile.industry && <span>{profile.industry}</span>}
              {profile.company_size && <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {profile.company_size}</span>}
              {profile.website && (
                <a href={profile.website} target="_blank" rel="noopener noreferrer" data-testid="company-profile-website-link" className="flex items-center gap-1 text-indigo-400 hover:text-indigo-300">
                  <Globe className="w-3 h-3" /> Website
                </a>
              )}
            </div>
          </div>
        </div>

        {profile.description && <p className="text-sm text-slate-300 mt-4 leading-relaxed" data-testid="company-profile-description">{profile.description}</p>}

        {profile.culture_points?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4" data-testid="company-profile-culture-points">
            {profile.culture_points.map((c, i) => (
              <span key={i} className="text-xs font-semibold bg-white/5 text-slate-300 px-3 py-1.5 rounded-full">{c}</span>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 mb-4">
        <Briefcase className="w-4 h-4 text-indigo-400" />
        <h2 className="font-heading text-lg font-semibold text-white">Open Roles ({profile.jobs?.length || 0})</h2>
      </div>

      {(!profile.jobs || profile.jobs.length === 0) ? (
        <div className="glass-panel rounded-2xl p-8 text-center" data-testid="company-profile-no-jobs">
          <p className="text-slate-400 text-sm">No active job postings from this company right now.</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="company-profile-jobs-list">
          {profile.jobs.map((j) => <JobCard key={j.id} job={j} onToggleSave={handleToggleSave} />)}
        </div>
      )}
    </div>
  );
}
